import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { NumericFormat as NumberFormat } from 'react-number-format';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import Checkbox from '@/components/UI/Form/Checkbox';
import EditIcon from '@/components/UI/Icons/EditIcon';
import { useCurrentAccountData } from '@/hooks/ReactQueryHooks/useCurrentAccountQuery';
import { isRealWorkspace, useEnsureCurrentWorkspace } from '@/hooks/useEnsureCurrentWorkspace';
import { AppRoutes, buildRoute } from '@/route/routeConfig';
import { loadSherpaTemplate } from '../../../../api/project';
import { postProject } from '../../../../api/Sherpa/Project';
import type { IPostProjectPayload } from '../../../../api/Sherpa/Project/modules/ProjectCore/types';
import { getUser } from '../../../../api/user';
import { projectsSelector, workspaceSelector } from '../../../../store';
import { clearProjectBaselineInfo } from '../../../../store/Projects/Project/BaselineProjectInfo';
import { clearProjectBudget } from '../../../../store/Projects/Project/ProjectBudget';
import { notifyUser } from '../../../../store/Toast';
import type { TProjectStages } from '../../../../types/Project';
import { getInitials, projectBudgetTotal, sanitizeResponseError } from '../../../../utils';
import ImageCircularAvatar from '../../../UI/Avatar/ImageCircularAvatar';
import Button from '../../../UI/Button';
import Loading from '../../../UI/loading';
import { CurrencyFormatDisplay } from '../../../UI/NumberFormatDisplay/CurrencyFormatDisplay';
import { MeasurementFormatDisplay } from '../../../UI/NumberFormatDisplay/MeasurementFormatDisplay';
import { PercentFormatDisplay } from '../../../UI/NumberFormatDisplay/PercentFormatDisplay';
import styles from './Form.module.css';

export default function FormReview({ handleStep }: { handleStep: (step: number) => void }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [hasChargeAgreed, setHasChargeAgreed] = useState(false);
  const [isDepartmentLedProject, setIsDepartmentLedProject] = useState(false);

  const { Project } = useSelector(projectsSelector);
  const { BaselineProjectInfo, ProjectBudget, ProjectTemplate } = Project;
  const { currentWorkspace } = useSelector(workspaceSelector);
  useEnsureCurrentWorkspace();
  const currentAccount = useCurrentAccountData({});
  const projectLeadId = Number(BaselineProjectInfo.projectLeadId?.value);

  //Tanstack: version 5 query implementation
  const fetchUser = useQuery({
    queryKey: ['user', projectLeadId],
    queryFn: () => getUser(projectLeadId),
    keepPreviousData: true,
    staleTime: 5000,
    enabled: Number.isInteger(projectLeadId) && projectLeadId > 0,
  });

  const projectLeadUser = fetchUser.data?.data;
  const createProject = useMutation((data: IPostProjectPayload) => postProject({ data }), {
    onSuccess: (res: any) => projectCreateSuccess(res),
    onError: (error: any) => projectCreateError(error),
  });

  const projectCreateSuccess = (res: any) => {
    navigate(buildRoute(AppRoutes.SHERPA_PROJECT_DETAIL, { projectId: res.data.id }), { replace: true });
    dispatch(
      notifyUser({
        showToast: true,
        toastMessage: `Project ${BaselineProjectInfo.name} create successful.`,
        toastType: 'success',
      })
    );
    if (ProjectTemplate.id) {
      loadSherpaTemplate({
        projectId: res.data.id,
        data: { projectTemplateId: ProjectTemplate.id },
      });
    }
    dispatch(clearProjectBaselineInfo());
    dispatch(clearProjectBudget());
  };

  const projectCreateError = (error: any) => {
    if (error?.response?.status === 401) {
      return;
    }
    const resError = sanitizeResponseError(error);
    dispatch(
      notifyUser({
        showToast: true,
        toastMessage: `Project ${BaselineProjectInfo.name} create failed. ${resError}`,
        toastType: 'failed',
      })
    );
  };

  const submitHandler = () => {
    if (currentAccount?.data?.data?.subscriptions?.includes('SHERPA_CO_OP') && !hasChargeAgreed) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'You need to agree the charges for creating a new project.',
          toastType: 'failed',
        })
      );
      return;
    }
    const budget = ProjectBudget.budget?.filter((d) => d?.name !== '').map(({ name, amount }) => ({ name, amount }));
    const sources = ProjectBudget.sources?.filter((d) => d?.name !== '').map(({ name, amount }) => ({ name, amount }));
    const uses = ProjectBudget.uses
      ?.filter((d) => d?.name !== '')
      .map(({ name, amount, source }) => ({
        name,
        amount,
        source,
      }));

    if (!isRealWorkspace(currentWorkspace)) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Select a program before creating a project.',
          toastType: 'failed',
        })
      );
      return;
    }

    if (!Number.isInteger(projectLeadId) || projectLeadId <= 0) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Select a valid project lead before creating a project.',
          toastType: 'failed',
        })
      );
      return;
    }

    const newProjectData: IPostProjectPayload = {
      name: BaselineProjectInfo.name,
      address: BaselineProjectInfo.address,
      city: BaselineProjectInfo.city,
      state: BaselineProjectInfo.state.name,
      zipCode: BaselineProjectInfo.zipCode,
      location: BaselineProjectInfo.location,
      projectType: BaselineProjectInfo.projectType.value,
      constructionType: BaselineProjectInfo.constructionType.value,
      squareFeet: BaselineProjectInfo.squareFeet,
      workspaceId: currentWorkspace.id,
      projectLeadId,
      goals: BaselineProjectInfo.goals,
      units: BaselineProjectInfo.units ? BaselineProjectInfo.units : undefined,
      avgSalesPrice: BaselineProjectInfo.avgSalesPrice ? BaselineProjectInfo.avgSalesPrice : undefined,
      stage: 'PRE_DEVELOPMENT' as TProjectStages,
      departmentLedProject: isDepartmentLedProject,
      neighborhood: BaselineProjectInfo.neighborhood?.value ? BaselineProjectInfo.neighborhood.value : undefined,
      budget: budget ?? [],
      sources: sources ?? [],
      uses: uses ?? [],
    };

    createProject.mutate(newProjectData);
  };
  return (
    <>
      {createProject.isLoading && <Loading loadingText='Creating New Project' />}
      <div className={styles.card}>
        <div className={styles['card-head-section']}>
          <h2 className={styles['card-title']}>Basic Project Information</h2>
          {/* <p className="mt-1 max-w-2xl text-sm text-gray-500">Personal details and application.</p> */}

          <div onClick={() => handleStep(0)}>
            <EditIcon />
          </div>
        </div>
        <div className={styles['card-body-section']}>
          <dl className='divide-y mx-8'>
            <div className='py-4 sm:grid sm:grid-cols-3 sm:gap-4'>
              <dt className='text-sm font-medium text-gray-500'>Project Lead</dt>

              {projectLeadUser && (
                <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 flex gap-4'>
                  <ImageCircularAvatar imageUrl={projectLeadUser.profileImageUrl} fallbackText={getInitials(projectLeadUser.name)} />
                  <div>
                    <p>{projectLeadUser.name}</p>
                    <span className='text-xs text-neutralColor-normal'>{projectLeadUser.title}</span>
                  </div>
                </dd>
              )}
            </div>
            <div className='py-4 sm:grid sm:grid-cols-3 sm:gap-4'>
              <dt className='text-sm font-medium text-gray-500'>Project Name</dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>{BaselineProjectInfo.name}</dd>
            </div>
            <div className='py-4 sm:grid sm:grid-cols-3 sm:gap-4'>
              <dt className='text-sm font-medium text-gray-500'>Address</dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>{BaselineProjectInfo.address}</dd>
            </div>
            <div className='py-4 sm:grid sm:grid-cols-3 sm:gap-4'>
              <dt className='text-sm font-medium text-gray-500'>City</dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>{BaselineProjectInfo.city}</dd>
            </div>
            <div className='py-4 sm:grid sm:grid-cols-3 sm:gap-4'>
              <dt className='text-sm font-medium text-gray-500'>State</dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>{BaselineProjectInfo.state.name}</dd>
            </div>
            <div className='py-4 sm:grid sm:grid-cols-3 sm:gap-4'>
              <dt className='text-sm font-medium text-gray-500'>Zip/Postal Code</dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>{BaselineProjectInfo.zipCode}</dd>
            </div>
            <div className='py-4 sm:grid sm:grid-cols-3 sm:gap-4'>
              <dt className='text-sm font-medium text-gray-500'>Project Type</dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>{BaselineProjectInfo.projectType.value}</dd>
            </div>
            {BaselineProjectInfo?.units && (
              <div className='py-4 sm:grid sm:grid-cols-3 sm:gap-4'>
                <dt className='text-sm font-medium text-gray-500'>Units</dt>
                <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                  <NumberFormat value={BaselineProjectInfo.units} displayType={'text'} thousandSeparator={true} />
                </dd>
              </div>
            )}
            {BaselineProjectInfo?.avgSalesPrice && (
              <div className='py-4 sm:grid sm:grid-cols-3 sm:gap-4'>
                <dt className='text-sm font-medium text-gray-500'>Average Sales Price</dt>
                <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                  <NumberFormat value={BaselineProjectInfo.avgSalesPrice} displayType={'text'} thousandSeparator={true} />
                </dd>
              </div>
            )}
            <div className='py-4 sm:grid sm:grid-cols-3 sm:gap-4'>
              <dt className='text-sm font-medium text-gray-500'>Construction Type</dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>{BaselineProjectInfo.constructionType.value}</dd>
            </div>
            {BaselineProjectInfo?.neighborhood?.value && (
              <div className='py-4 sm:grid sm:grid-cols-3 sm:gap-4'>
                <dt className='text-sm font-medium text-gray-500'>Neighborhood</dt>
                <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>{BaselineProjectInfo?.neighborhood?.value}</dd>
              </div>
            )}
            <div className='py-4 sm:grid sm:grid-cols-3 sm:gap-4'>
              <dt className='text-sm font-medium text-gray-500'>Square Feet</dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                <MeasurementFormatDisplay value={BaselineProjectInfo.squareFeet} suffix=' Sq. Ft' />
              </dd>
            </div>
            <div className='py-4 sm:grid sm:grid-cols-3 sm:gap-4'>
              <dt className='text-sm font-bold text-primaryColor-normal'>Social and Minority Goals</dt>
            </div>
            {BaselineProjectInfo.goals.map((goal, i) => (
              <div key={i} className={`px-4 py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                <dt className='text-sm font-medium text-gray-500'>{goal.name} (%)</dt>
                <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                  <PercentFormatDisplay value={goal.percentage} />
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
      <div className={styles.card}>
        <div className={styles['card-head-section']}>
          <h2 className={styles['card-title']}>Project Template</h2>
          <div onClick={() => handleStep(1)}>
            <EditIcon />
          </div>
        </div>
        <div className={styles['card-body-section']}>
          <dl className='mx-8'>
            <div className={'py-4 sm:grid sm:grid-cols-3 sm:gap-4'}>
              <dt className='text-sm font-medium text-gray-500'>Template</dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>{ProjectTemplate.name}</dd>
            </div>
          </dl>
        </div>
      </div>
      <div className={styles.card}>
        <div className={styles['card-head-section']}>
          <h2 className={styles['card-title']}>Project Sources</h2>
          <div onClick={() => handleStep(2)}>
            <EditIcon />
          </div>
        </div>
        <div className={styles['card-body-section']}>
          <dl className='mx-8 divide-y'>
            {ProjectBudget.sources.map((source, i) => (
              <div key={i} className={'py-4 sm:grid sm:grid-cols-3 sm:gap-4'}>
                <dt className='text-sm font-medium text-gray-500'>{source.name}</dt>
                <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                  <CurrencyFormatDisplay value={source.amount} prefix='$ ' />
                </dd>
              </div>
            ))}
          </dl>
          <dl className='mx-8 divide-y'>
            <div className={'py-4 sm:grid sm:grid-cols-3 sm:gap-4'}>
              <dt className='text-sm font-medium text-gray-500'>Total Project Sources</dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                <CurrencyFormatDisplay value={projectBudgetTotal(ProjectBudget.sources)} prefix='$ ' />
              </dd>
            </div>
          </dl>
        </div>
      </div>
      <div className={styles.card}>
        <div className={styles['card-head-section']}>
          <h2 className={styles['card-title']}>Project Uses</h2>
          <div onClick={() => handleStep(3)}>
            <EditIcon />
          </div>
        </div>
        <div className={styles['card-body-section']}>
          <dl className='mx-8 divide-y'>
            {ProjectBudget.uses.map((use, i) => (
              <div key={i} className={'py-4 sm:grid sm:grid-cols-3 sm:gap-4'}>
                <dt className='text-sm font-medium text-gray-500'>{use.name}</dt>
                <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                  <CurrencyFormatDisplay value={use.amount} prefix='$ ' />
                </dd>
              </div>
            ))}
          </dl>
          <dl className='mx-8 divide-y'>
            <div className={'py-4 sm:grid sm:grid-cols-3 sm:gap-4'}>
              <dt className='text-sm font-medium text-gray-500'>Total Project Uses</dt>
              <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
                <CurrencyFormatDisplay value={projectBudgetTotal(ProjectBudget.uses)} prefix='$ ' />
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {currentAccount?.data?.data?.subscriptions.includes('SHERPA_CO_OP') && (
        <div className='mb-10'>
          <Checkbox
            name='charge_agree'
            checked={hasChargeAgreed}
            id='charge-agree'
            label='By checking this box, you agree to pay the monthly fee of $500 per month for this project. Should a project be started after the first of the month, then the first month shall be prorated accordingly but each subsequent month shall be at a cost of $500 per month.'
            onChange={(e) => {
              const { checked } = e.target;
              setHasChargeAgreed(checked);
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          />
        </div>
      )}

      <div className='mb-10'>
        <Checkbox
          name='departmentLedProject'
          checked={isDepartmentLedProject}
          id='department-led-project'
          label='This is a Department Led Project'
          onChange={(e) => {
            const { checked } = e.target;
            setIsDepartmentLedProject(checked);
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        />
      </div>

      <div className='w-full flex justify-end gap-4'>
        <Button type='button' btnStyle='btn-neutral' label='Previous' onClick={() => handleStep(3)} />
        <Button type='button' btnStyle='btn-primary' label='Submit' onClick={submitHandler} />
      </div>
    </>
  );
}
